import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ProfileHeader } from '@/components/profile/ProfileHeader';
import { PersonalInfo } from '@/components/profile/PersonalInfo';
import { ProfessionalInfo } from '@/components/profile/ProfessionalInfo';
import { GallerySection } from '@/components/profile/GallerySection';
import { UserProfile } from '@/types/user';

const initialProfile: UserProfile = {
  name: '',
  role: '',
  specialties: [],
  city: '',
  address: '',
  services: '',
  phone: '',
  email: '',
  description: '',
  gallery: []
};

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile>(initialProfile);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const loadProfile = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate('/');
        return;
      }

      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data() as UserProfile;
          // Ensure all fields have at least empty string values
          setProfile({
            ...initialProfile,
            ...data
          });
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const handleSave = async () => {
    if (!auth.currentUser) return;

    try {
      const docRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(docRef, profile);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleUpdateField = (field: string, value: string) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSpecialty = (specialty: string) => {
    setProfile(prev => ({
      ...prev,
      specialties: [...(prev.specialties || []), specialty]
    }));
  };

  const handleRemoveSpecialty = (index: number) => {
    setProfile(prev => ({
      ...prev,
      specialties: prev.specialties.filter((_, i) => i !== index)
    }));
  };

  const handleAddImage = (image: { url: string; description: string }) => {
    setProfile(prev => ({
      ...prev,
      gallery: [...(prev.gallery || []), { ...image, id: Date.now().toString() }]
    }));
  };

  const handleRemoveImage = (id: string) => {
    setProfile(prev => ({
      ...prev,
      gallery: prev.gallery?.filter(img => img.id !== id) || []
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
        >
          ← Voltar ao Dashboard
        </Button>

        <Card className="p-6">
          <ProfileHeader
            profile={profile}
            isEditing={isEditing}
            onSave={handleSave}
            onEdit={() => setIsEditing(true)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PersonalInfo
              profile={profile}
              isEditing={isEditing}
              onUpdate={handleUpdateField}
            />
            <ProfessionalInfo
              profile={profile}
              isEditing={isEditing}
              onUpdate={handleUpdateField}
              onAddSpecialty={handleAddSpecialty}
              onRemoveSpecialty={handleRemoveSpecialty}
            />
          </div>
        </Card>

        <Card className="p-6">
          <GallerySection
            images={profile.gallery || []}
            isEditing={isEditing}
            onAddImage={handleAddImage}
            onRemoveImage={handleRemoveImage}
          />
        </Card>
      </div>
    </div>
  );
}